import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";

import {
  useSubscriptions,
  useUpsertSubscription,
  useDeleteSubscription,
  computeSubscriptionTotals,
  useAllPlans,
  type Subscription,
  useServiceCatalog,
  useServicePlans,
  useAvailableCountries,
  type ServiceWithCategory,
  type ServicePlan,
} from "@/lib/queries";
import {
  CATEGORIES,
  BILLING_CYCLES,
  type CategoryValue,
  type BillingCycle,
} from "@/lib/categories";
import {
  formatMoney,
  formatDate,
  dateFnsLocale,
  monthlyEquivalent,
  formatNok,
  formatMoneyEdit,
} from "@/lib/format";
import { moneyInputProps, sanitizeMoney, normalizeMoneyForZod } from "@/lib/money-input";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Pencil,
  Trash2,
  X,
  Search,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { InfoTooltip } from "@/components/InfoTooltip";
import { FreeLimitNudge } from "@/components/FreeLimitNudge";
import {
  FREE_VISIBLE_LIMIT,
  rankSubscriptionsForGate,
  topNIds,
  useIsFree,
} from "@/lib/free-limits";
import { BlurredGateWrapper, GateStatsNote, UpgradeGateRow } from "@/components/UpgradeGate";
import { SubscriptionCreepChart, CategoryBreakdown } from "@/components/AnalyticsCharts";
import { useInflation, useSalary, cumulativeCpiFactor } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/subscriptions")({
  head: () => ({
    meta: [
      { title: "Subscriptions — VISIKT" },
      { name: "description", content: "Your recurring subscriptions, categorised and totalled." },
    ],
  }),
  validateSearch: (search): { new?: number } => ({
    new: search.new ? Number(search.new) : undefined,
  }),
  component: SubscriptionsPage,
});

const COUNTRY_STORAGE_KEY = "visikt:preferredCountry";
const LEGACY_COUNTRY_STORAGE_KEY = "sparr:preferredCountry";

function migrateCountryStorageKey() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(COUNTRY_STORAGE_KEY) != null) return;
    const oldValue = window.localStorage.getItem(LEGACY_COUNTRY_STORAGE_KEY);
    if (oldValue != null) {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, oldValue);
      window.localStorage.removeItem(LEGACY_COUNTRY_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}
const DEFAULT_COUNTRY = "NO";
const COUNTRY_DEFAULT_CURRENCY: Record<string, string> = {
  NO: "NOK",
  SE: "SEK",
  DK: "DKK",
};

function SubscriptionsPage() {
  const { t } = useTranslation();
  const subs = useSubscriptions();
  const plans = useAllPlans();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/subscriptions" }) as { new?: number };
  const [editing, setEditing] = useState<Subscription | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<CategoryValue | "all">("all");
  const [status, setStatus] = useState<"active" | "paused" | "unknown" | "all">("active");
  const [sortBy, setSortBy] = useState<"cost_desc" | "cost_asc" | "name_asc" | "date_desc">(
    "cost_desc",
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pageHeight, setPageHeight] = useState<number | null>(null);
  const FALLBACK_PAGE_HEIGHT = 540;
  const upsertSub = useUpsertSubscription();
  const infl = useInflation();
  const sal = useSalary();

  // Auto-open create dialog when navigated with ?new=1
  useEffect(() => {
    if (search?.new) {
      setCreating(true);
      navigate({ to: "/subscriptions", replace: true, search: {} as never });
    }
  }, [search?.new, navigate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 });
    setCurrentPage(0);
  }, [filter, status, sortBy]);

  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el) return;
    const measure = () => setPageHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [subs.data?.length, filter, status, sortBy]);

  const scrollToPage = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const h = pageHeight ?? FALLBACK_PAGE_HEIGHT;
    el.scrollTo({ top: i * h, behavior: "smooth" });
  };

  const planById = useMemo(() => {
    const m = new Map<string, ServicePlan>();
    for (const p of plans.data ?? []) m.set(p.id, p);
    return m;
  }, [plans.data]);

  const allSubs = useMemo(() => subs.data ?? [], [subs.data]);
  const isFree = useIsFree();
  const gateIds = useMemo(
    () => (isFree ? topNIds(rankSubscriptionsForGate(allSubs), FREE_VISIBLE_LIMIT) : null),
    [isFree, allSubs],
  );
  const hiddenCount = isFree ? Math.max(0, allSubs.length - FREE_VISIBLE_LIMIT) : 0;
  const subsForStats = useMemo(
    () => (gateIds ? allSubs.filter((s) => gateIds.has(s.id)) : allSubs),
    [allSubs, gateIds],
  );
  const usedCategories = useMemo(() => {
    const set = new Set<CategoryValue>();
    for (const s of allSubs) set.add(s.category);
    return set;
  }, [allSubs]);
  const scopedSubs = useMemo(() => {
    let out = allSubs.filter((s) => filter === "all" || s.category === filter);
    if (status === "active") out = out.filter((s) => s.is_active === true);
    else if (status === "paused") out = out.filter((s) => s.is_active === false);
    else if (status === "unknown") out = out.filter((s) => s.is_active === null);
    const sorted = [...out].sort((a, b) => {
      if (sortBy === "name_asc") return a.name.localeCompare(b.name);
      if (sortBy === "date_desc") return (b.start_date ?? "").localeCompare(a.start_date ?? "");
      const am = monthlyEquivalent(Number(a.monthly_cost_nok_equiv), a.billing_cycle);
      const bm = monthlyEquivalent(Number(b.monthly_cost_nok_equiv), b.billing_cycle);
      return sortBy === "cost_asc" ? am - bm : bm - am;
    });
    return sorted;
  }, [allSubs, filter, status, sortBy]);
  const scopedSubsForStats = gateIds ? scopedSubs.filter((s) => gateIds.has(s.id)) : scopedSubs;
  const totals = computeSubscriptionTotals(subsForStats);
  const scopedTotals = computeSubscriptionTotals(scopedSubsForStats);
  const inactive = subsForStats.filter((s) => !s.is_active);
  const savingsMonthly = inactive.reduce(
    (acc, s) => acc + monthlyEquivalent(Number(s.monthly_cost_nok_equiv), s.billing_cycle),
    0,
  );
  const filtered = scopedSubs;

  // Real (CPI-adjusted) subs cost, deflated to earliest tracked salary year.
  const realCost = useMemo(() => {
    const salRows = sal.data ?? [];
    if (salRows.length === 0 || totals.monthly === 0) return null;
    const baseYear = Math.min(...salRows.map((r) => new Date(r.effective_date).getFullYear()));
    const now = new Date().getFullYear();
    const factor = cumulativeCpiFactor(infl.data ?? [], baseYear, now);
    if (factor <= 0) return null;
    return { baseYear, value: totals.monthly / factor };
  }, [sal.data, infl.data, totals.monthly]);

  const distinctStartMonths = useMemo(() => {
    const set = new Set<string>();
    for (const s of allSubs)
      if (s.is_active === true && s.start_date) set.add(s.start_date.slice(0, 7));
    return set.size;
  }, [allSubs]);

  async function applyCatalogPrice(s: Subscription, newPrice: number) {
    try {
      await upsertSub.mutateAsync({
        id: s.id,
        monthly_cost_nok: newPrice,
        source_price_nok: newPrice,
      });
      toast.success(t("subsExtra.priceUpdated"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.somethingWrong"));
    }
  }

  return (
    <AppShell>
      <section className="animate-entry">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {t("subs.monthlySpend")}
        </p>
        <h1 className="text-5xl font-display font-bold tracking-tight tabular-nums leading-none">
          {formatMoney(totals.monthly)}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t("subs.activeYear", {
            count: totals.activeCount,
            yearly: formatMoney(totals.yearly),
          })}
        </p>
        {isFree && hiddenCount > 0 && <GateStatsNote />}
      </section>

      <section>
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            label={t("subs.statMonthly")}
            value={formatMoney(scopedTotals.monthly)}
            sub={`${scopedTotals.activeCount} ${scopedTotals.activeCount === 1 ? t("subs.one") : t("subs.many")}`}
          />
          <StatCard
            label={t("subs.statAnnual")}
            value={formatMoney(scopedTotals.yearly)}
            sub={t("subs.runRate")}
            tone="accent"
          />
          <StatCard
            label={t("subs.statSavings")}
            value={formatMoney(savingsMonthly)}
            sub={t("subs.unused", { n: inactive.length })}
            tone="positive"
          />
        </div>
        {realCost && (
          <div className="mt-3 rounded-2xl border border-border px-4 py-3 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1 inline-flex items-center gap-1">
                {t("subs.realCostTitle")}
                <InfoTooltip text={t("subs.realCostHelp")} label={t("subs.realCostTitle")} />
              </div>
              <div className="text-[10px] font-mono text-muted-foreground">
                {t("subs.inBaseYearKroner", { year: realCost.baseYear })}
              </div>
            </div>
            <div className="text-lg font-display font-bold tabular-nums">
              {formatNok(realCost.value)}
              <span className="text-[10px] font-mono font-normal text-muted-foreground">
                {t("common.perMonth")}
              </span>
            </div>
          </div>
        )}
      </section>

      <section>
        {distinctStartMonths >= 3 ? (
          <div className="rounded-2xl border border-border p-4">
            <div className="flex items-baseline justify-between mb-3">
              <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
                {t("subs.creepTitle")}
                <InfoTooltip text={t("subs.creepHelp")} label={t("subs.creepTitle")} />
              </span>
            </div>
            <SubscriptionCreepChart subs={allSubs} ariaLabel={t("subs.creepTitle")} />
          </div>
        ) : totals.byCategory.length > 0 ? (
          <div className="rounded-2xl border border-border p-4">
            <div className="flex items-baseline justify-between mb-3">
              <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
                {t("subs.breakdownTitle")}
                <InfoTooltip text={t("subs.breakdownHelp")} label={t("subs.breakdownTitle")} />
              </span>
            </div>
            <CategoryBreakdown rows={totals.byCategory} labelFor={(c) => t(`categories.${c}`)} />
          </div>
        ) : (
          <div className="rounded-2xl border border-border px-5 py-6 text-center text-sm text-muted-foreground">
            {t("subs.trendEmpty")}
          </div>
        )}
      </section>

      <FreeLimitNudge kind="subs" count={allSubs.length} limit={3} />

      <section className="space-y-3">
        {/* Status + Category + Sort */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="sub-status"
              className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
            >
              {t("subs.statusLabel")}
            </label>
            <select
              id="sub-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className="w-full text-[11px] font-mono uppercase tracking-widest border border-border rounded-full bg-transparent px-3 py-1.5 text-muted-foreground hover:text-foreground truncate"
            >
              {(["active", "paused", "unknown", "all"] as const).map((k) => (
                <option key={k} value={k}>
                  {t(`subs.filter${k[0].toUpperCase()}${k.slice(1)}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="sub-cat"
              className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
            >
              {t("subs.categoryLabel")}
            </label>
            <select
              id="sub-cat"
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="w-full text-[11px] font-mono uppercase tracking-widest border border-border rounded-full bg-transparent px-3 py-1.5 text-muted-foreground hover:text-foreground truncate"
            >
              <option value="all">{t("subs.categoryAll")}</option>
              {CATEGORIES.filter((c) => usedCategories.has(c.value)).map((c) => (
                <option key={c.value} value={c.value}>
                  {t(`categories.${c.value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="sub-sort"
              className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
            >
              {t("subs.sortLabel")}
            </label>
            <select
              id="sub-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="w-full text-[11px] font-mono uppercase tracking-widest border border-border rounded-full bg-transparent px-3 py-1.5 text-muted-foreground hover:text-foreground truncate"
            >
              <option value="cost_desc">{t("subs.sortCostDesc")}</option>
              <option value="cost_asc">{t("subs.sortCostAsc")}</option>
              <option value="name_asc">{t("subs.sortNameAsc")}</option>
              <option value="date_desc">{t("subs.sortDateDesc")}</option>
            </select>
          </div>
        </div>

        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {t("subs.filterSummary", {
            count:
              scopedTotals.activeCount +
              (status === "paused" || status === "all"
                ? scopedSubs.filter((s) => s.is_active === false).length
                : 0) +
              (status === "unknown" || status === "all"
                ? scopedSubs.filter((s) => s.is_active === null).length
                : 0),
            value: formatNok(scopedTotals.monthly),
          })}
        </p>
      </section>

      <section>
        <div className="flex items-end justify-between mb-3 gap-3">
          <h2 className="text-lg font-display font-bold tracking-tight">{t("subs.listTitle")}</h2>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {t("subs.total", { n: allSubs.length })}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => scrollToPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  aria-label={t("common.previous")}
                  className="size-7 grid place-items-center rounded-full border border-border/30 bg-card text-foreground/80 hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronUp className="size-4" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollToPage(Math.min(pageCount - 1, currentPage + 1))}
                  disabled={currentPage >= pageCount - 1}
                  aria-label={t("common.next")}
                  className="size-7 grid place-items-center rounded-full border border-border/30 bg-card text-foreground/80 hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronDown className="size-4" strokeWidth={1.75} />
                </button>
              </div>
            )}
          </div>
        </div>
        {subs.isLoading ? (
          <p className="text-sm text-muted-foreground">{t("subs.loading")}</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("subs.emptyFilter")}</p>
        ) : (
          <div className="space-y-3">
            <div
              ref={scrollRef}
              onScroll={(e) => {
                const top = (e.target as HTMLDivElement).scrollTop;
                const h = pageHeight ?? FALLBACK_PAGE_HEIGHT;
                const idx = Math.round(top / h);
                if (idx !== currentPage) setCurrentPage(idx);
              }}
              className="overflow-y-auto snap-y snap-mandatory overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              style={{
                height: pageHeight ?? FALLBACK_PAGE_HEIGHT,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {(() => {
                const items: { key: string; node: React.ReactNode }[] = [];
                let ctaInserted = false;
                filtered.forEach((s) => {
                  const currency = s.currency ?? "NOK";
                  const cycleLabel = t(`cycles.${s.billing_cycle}`);
                  const monthly = monthlyEquivalent(Number(s.monthly_cost_nok), s.billing_cycle);
                  const linkedPlan = s.source_plan_id ? planById.get(s.source_plan_id) : null;
                  const drift =
                    linkedPlan &&
                    s.source_price_nok != null &&
                    Number(linkedPlan.monthly_price_nok) !== Number(s.source_price_nok)
                      ? Number(linkedPlan.monthly_price_nok)
                      : null;
                  const gated = gateIds != null && !gateIds.has(s.id);

                  const rowInner = (
                    <div className="w-full rounded-2xl border border-border/30 bg-card px-4 py-3 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 text-left">
                      <div className="size-10 rounded-full bg-muted border border-border grid place-items-center shrink-0">
                        <span className="text-[11px] font-mono font-medium">
                          {s.name.slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate flex items-center gap-2">
                          <span className="truncate">{s.name}</span>
                          {s.is_active === false && (
                            <span className="text-[9px] font-mono uppercase text-muted-foreground border border-border px-1.5 py-0.5 rounded shrink-0">
                              {t("subs.pause")}
                            </span>
                          )}
                          {s.is_active === null && (
                            <span className="text-[9px] font-mono uppercase text-muted-foreground border border-border px-1.5 py-0.5 rounded shrink-0">
                              {t("subs.unknown")}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-muted-foreground truncate">
                          {t(`categories.${s.category}`)} · {t("subs.next")}: —
                        </div>
                        {drift != null && !gated && (
                          <div className="mt-1.5 inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                applyCatalogPrice(s, drift);
                              }}
                              className="inline-flex items-center gap-1 text-[10px] font-mono text-[color:var(--color-accent)] hover:underline"
                            >
                              <AlertCircle className="size-3" strokeWidth={1.5} />
                              {t("subsExtra.catalogUpdated")} ·{" "}
                              {t("subsExtra.updateTo", { value: formatMoney(drift, currency) })}
                            </button>
                            <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                              <InfoTooltip
                                text={t("subsExtra.catalogPriceChangedHelp")}
                                label={t("subsExtra.catalogUpdated")}
                              />
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-display font-bold tabular-nums">
                          {formatMoney(monthly, currency, { decimals: true })}
                          <span className="text-[10px] font-mono font-normal text-muted-foreground">
                            {t("common.perMonth")}
                          </span>
                        </div>
                        <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">
                          {cycleLabel} ·{" "}
                          {formatMoney(Number(s.monthly_cost_nok), currency, { decimals: true })}
                        </div>
                      </div>
                    </div>
                  );

                  if (gated) {
                    items.push({
                      key: s.id,
                      node: <BlurredGateWrapper>{rowInner}</BlurredGateWrapper>,
                    });
                    if (!ctaInserted && hiddenCount > 0) {
                      items.push({
                        key: "__gate_cta__",
                        node: <UpgradeGateRow hiddenCount={hiddenCount} kind="subs" />,
                      });
                      ctaInserted = true;
                    }
                  } else {
                    items.push({
                      key: s.id,
                      node: (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setEditing(s)}
                          onKeyDown={(ev) => {
                            if (ev.key === "Enter" || ev.key === " ") {
                              ev.preventDefault();
                              setEditing(s);
                            }
                          }}
                          className="w-full text-left hover:brightness-110 transition cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
                        >
                          {rowInner}
                        </div>
                      ),
                    });
                  }
                });

                const PAGE_SIZE = 6;
                const pages: { key: string; node: React.ReactNode }[][] = [];
                for (let i = 0; i < items.length; i += PAGE_SIZE) {
                  pages.push(items.slice(i, i + PAGE_SIZE));
                }
                if (pages.length !== pageCount) {
                  queueMicrotask(() => setPageCount(pages.length));
                }

                return pages.map((page, idx) => (
                  <div
                    key={idx}
                    ref={idx === 0 ? pageRef : undefined}
                    className="snap-start"
                    style={pageHeight != null ? { minHeight: pageHeight } : undefined}
                  >
                    <ul className="space-y-2">
                      {page.map((item) => (
                        <li key={item.key}>{item.node}</li>
                      ))}
                    </ul>
                  </div>
                ));
              })()}
            </div>
            {pageCount > 1 && (
              <div className="flex items-center justify-center gap-1.5">
                {Array.from({ length: pageCount }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => scrollToPage(i)}
                    className={cn(
                      "size-2 rounded-full transition-colors",
                      currentPage === i
                        ? "bg-foreground"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50",
                    )}
                    aria-label={t("subs.pageIndicator", { current: i + 1, total: pageCount })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {(creating || editing) && (
        <SubscriptionDialog
          initial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(wasNew) => {
            setCreating(false);
            setEditing(null);
            if (wasNew) navigate({ to: "/dashboard" });
          }}
        />
      )}
    </AppShell>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "accent" | "positive";
}) {
  const valueColor =
    tone === "accent"
      ? "text-[color:var(--color-accent)]"
      : tone === "positive"
        ? "text-emerald-500"
        : "text-foreground";
  return (
    <div className="rounded-2xl border border-border px-3 py-3 min-w-0">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-2 truncate">
        {label}
      </div>
      <div
        className={`text-lg font-display font-bold tabular-nums leading-tight truncate ${valueColor}`}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-2 text-[10px] font-mono text-muted-foreground truncate">{sub}</div>
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "whitespace-nowrap px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-colors " +
        (active
          ? "bg-foreground text-background border-foreground"
          : "bg-transparent text-muted-foreground border-border hover:text-foreground")
      }
    >
      {children}
    </button>
  );
}

/* =================== Dialog =================== */

type DialogMode = "catalog" | "manual";
type FormSource = "manual" | "catalog" | "edit";

interface FormState {
  source: FormSource;
  name: string;
  category: CategoryValue;
  cost: string;
  cycle: BillingCycle;
  currency: string;
  startDate: string;
  isActive: "" | "active" | "paused" | "unknown";
  notes: string;
  sourcePlanId: string | null;
  sourcePriceNok: number | null;
}

function emptyForm(source: FormSource): FormState {
  return {
    source,
    name: "",
    category: "streaming",
    cost: "",
    cycle: "monthly",
    currency: "NOK",
    startDate: new Date().toISOString().slice(0, 10),
    isActive: "",
    notes: "",
    sourcePlanId: null,
    sourcePriceNok: null,
  };
}

function fromSubscription(s: Subscription): FormState {
  return {
    source: "edit",
    name: s.name,
    category: s.category,
    cost: formatMoneyEdit(s.monthly_cost_nok),
    cycle: s.billing_cycle,
    currency: s.currency ?? "NOK",
    startDate: s.start_date,
    isActive: s.is_active === true ? "active" : s.is_active === false ? "paused" : "unknown",
    notes: s.notes ?? "",
    sourcePlanId: s.source_plan_id ?? null,
    sourcePriceNok: s.source_price_nok ?? null,
  };
}

function SubscriptionDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: Subscription | null;
  onClose: () => void;
  onSaved?: (wasNew: boolean) => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertSubscription();
  const del = useDeleteSubscription();

  const isEdit = !!initial;
  const [dialogMode, setDialogMode] = useState<DialogMode>("catalog");
  const [dateOpen, setDateOpen] = useState(false);
  const [country, setCountry] = useState<string>(() => {
    if (typeof window === "undefined") return DEFAULT_COUNTRY;
    try {
      migrateCountryStorageKey();
      return window.localStorage.getItem(COUNTRY_STORAGE_KEY) ?? DEFAULT_COUNTRY;
    } catch {
      return DEFAULT_COUNTRY;
    }
  });
  const [selectedService, setSelectedService] = useState<ServiceWithCategory | null>(null);
  const [form, setForm] = useState<FormState>(() =>
    initial ? fromSubscription(initial) : emptyForm("manual"),
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const countries = useAvailableCountries();
  const services = useServiceCatalog(country);
  const plansForService = useServicePlans(selectedService?.id ?? null);
  const allPlans = useAllPlans();

  // Plan count per service id (for "X plans" hint in service dropdown)
  const planCountByService = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of allPlans.data ?? []) {
      m.set(p.service_id, (m.get(p.service_id) ?? 0) + 1);
    }
    return m;
  }, [allPlans.data]);

  function changeCountry(next: string) {
    setCountry(next);
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setSelectedService(null);
  }

  function applyPlan(service: ServiceWithCategory, plan: ServicePlan) {
    const priceNok = Number(plan.monthly_price_nok);
    setForm({
      source: "catalog",
      name: `${service.name} — ${plan.name}`,
      category: service.category_key,
      cost: formatMoneyEdit(priceNok),
      cycle: plan.billing_cycle,
      currency: plan.currency || COUNTRY_DEFAULT_CURRENCY[service.country] || "NOK",
      startDate: new Date().toISOString().slice(0, 10),
      isActive: "",
      notes: form.notes,
      sourcePlanId: plan.id,
      sourcePriceNok: priceNok,
    });
  }

  // Auto-select first plan whenever a new service is chosen
  useEffect(() => {
    if (!selectedService) return;
    const list = plansForService.data ?? [];
    if (list.length === 0) return;
    if (form.sourcePlanId && list.some((p) => p.id === form.sourcePlanId)) return;
    applyPlan(selectedService, list[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService?.id, plansForService.data]);

  async function handleDelete() {
    if (!initial) return;
    if (!window.confirm(t("subs.deleteConfirm", { name: initial.name }))) return;
    try {
      await del.mutateAsync(initial.id);
      toast.success(t("subs.deleted"));
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.somethingWrong"));
    }
  }

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t("subs.validation.nameRequired")).max(120),
        category: z.enum(CATEGORIES.map((c) => c.value) as [CategoryValue, ...CategoryValue[]]),
        monthly_cost_nok: z.preprocess(
          normalizeMoneyForZod,
          z
            .number({ invalid_type_error: t("subs.validation.costPositive") })
            .min(0, t("subs.validation.costPositive"))
            .max(1000000, t("subs.validation.costPositive")),
        ),
        billing_cycle: z.enum(
          BILLING_CYCLES.map((c) => c.value) as [BillingCycle, ...BillingCycle[]],
        ),
        currency: z.string().trim().length(3, t("subs.validation.currencyLen")),
        start_date: z.string().min(1),
        is_active: z
          .enum(["", "active", "paused", "unknown"] as const)
          .refine((v) => v !== "", { message: t("subs.validation.statusRequired") })
          .transform((v) => (v === "active" ? true : v === "paused" ? false : null)),
        notes: z.string().max(500).optional().nullable(),
      }),
    [t],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({
      name: form.name,
      category: form.category,
      monthly_cost_nok: form.cost,
      billing_cycle: form.cycle,
      currency: form.currency.toUpperCase(),
      start_date: form.startDate,
      is_active: form.isActive,
      notes: form.notes || null,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    try {
      await upsert.mutateAsync({
        id: initial?.id,
        ...parsed.data,
        source_plan_id: form.sourcePlanId,
        source_price_nok: form.sourcePriceNok,
      });
      toast.success(initial ? t("subs.updated") : t("subs.added"));
      const wasNew = !initial;
      if (onSaved) onSaved(wasNew);
      else onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("common.somethingWrong"));
    }
  }

  const showCatalog = !isEdit && dialogMode === "catalog";

  return (
    <div
      className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-background border-t sm:border border-border sm:rounded-2xl max-h-[92vh] overflow-y-auto animate-entry"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-border">
          <h2 className="text-xl font-display font-bold tracking-tight truncate">
            {isEdit ? t("subs.editTitle") : t("subs.trackTitle")}
          </h2>
          <div className="flex items-center gap-3 shrink-0">
            {!isEdit && (
              <button
                type="button"
                onClick={() => {
                  if (dialogMode === "catalog") {
                    setDialogMode("manual");
                  } else {
                    setDialogMode("catalog");
                  }
                }}
                className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              >
                {dialogMode === "catalog" ? t("subs.enterManually") : t("subs.useCatalog")}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label={t("common.close")}
              className="size-8 grid place-items-center text-muted-foreground hover:text-foreground -mr-2"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Catalog card */}
          {showCatalog && (
            <div className="rounded-xl border border-border bg-card/40 p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <Field label={t("subs.country")}>
                  <select
                    className="input-base"
                    value={country}
                    onChange={(e) => changeCountry(e.target.value)}
                  >
                    {(countries.data && countries.data.length > 0
                      ? countries.data
                      : [DEFAULT_COUNTRY]
                    ).map((c) => (
                      <option key={c} value={c}>
                        {t(`countries.${c}` as const, { defaultValue: c })} ·{" "}
                        {COUNTRY_DEFAULT_CURRENCY[c] ?? "—"}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={t("subs.section.service")}>
                  <ServiceCombobox
                    services={services.data ?? []}
                    loading={services.isLoading}
                    value={selectedService}
                    planCounts={planCountByService}
                    onPick={(svc) => setSelectedService(svc)}
                  />
                </Field>
              </div>

              {selectedService && (
                <div className="space-y-2">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    {t("subs.section.plan")}
                  </div>
                  <PlanPills
                    plans={plansForService.data ?? []}
                    loading={plansForService.isLoading}
                    selectedId={form.sourcePlanId}
                    countryCurrency={COUNTRY_DEFAULT_CURRENCY[selectedService.country] ?? "NOK"}
                    onPick={(plan) => applyPlan(selectedService, plan)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Fields */}
          <div className="space-y-4">
            <Field label={t("subs.fields.name")}>
              <input
                className="input-base"
                required
                maxLength={120}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isEdit ? undefined : "Netflix — Standard"}
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="min-w-0 sm:col-span-2">
                <Field label={t("subs.fields.cost")}>
                  <input
                    className="input-base w-full min-w-0 tabular-nums"
                    {...moneyInputProps}
                    required
                    value={form.cost}
                    onChange={(e) => setForm({ ...form, cost: sanitizeMoney(e.target.value) })}
                  />
                </Field>
              </div>
              <div className="min-w-0">
                <Field label={t("subs.fields.currency")}>
                  <input
                    className="input-base w-full min-w-0 uppercase"
                    required
                    maxLength={3}
                    minLength={3}
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  />
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <Field label={t("subs.fields.cycle")}>
                  <select
                    className="input-base w-full min-w-0"
                    value={form.cycle}
                    onChange={(e) => setForm({ ...form, cycle: e.target.value as BillingCycle })}
                  >
                    {BILLING_CYCLES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {t(`cycles.${c.value}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="min-w-0">
                <Field label={t("subs.fields.category")}>
                  <select
                    className="input-base w-full min-w-0"
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value as CategoryValue })
                    }
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {t(`categories.${c.value}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0 overflow-hidden">
                <Field label={t("subs.fields.startDate")}>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="input-base w-full min-w-0 max-w-full flex items-center text-left"
                      >
                        <span className="truncate">
                          {form.startDate
                            ? formatDate(parseISO(form.startDate))
                            : t("subs.fields.startDate")}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        locale={dateFnsLocale()}
                        selected={form.startDate ? parseISO(form.startDate) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, "0");
                            const d = String(date.getDate()).padStart(2, "0");
                            setForm({ ...form, startDate: `${y}-${m}-${d}` });
                            setDateOpen(false);
                          }
                        }}
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
              </div>
              <div className="min-w-0 overflow-hidden">
                <Field label={t("subs.fields.active")}>
                  <select
                    className="input-base w-full min-w-0 max-w-full"
                    value={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.value as FormState["isActive"] })
                    }
                  >
                    <option value="">{t("subs.statusPlaceholder")}</option>
                    <option value="active">{t("subs.fields.active")}</option>
                    <option value="paused">{t("subs.pause")}</option>
                    <option value="unknown">{t("subs.unknown")}</option>
                  </select>
                </Field>
              </div>
            </div>

            <Field label="Notes">
              <textarea
                className="input-base min-h-[80px] resize-y"
                maxLength={500}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-1">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                aria-label={t("common.delete")}
                className="size-12 grid place-items-center rounded-md border border-border text-[color:var(--color-negative)] hover:bg-foreground/5"
              >
                <Trash2 className="size-4" strokeWidth={1.5} />
              </button>
            )}
            <button
              type="submit"
              disabled={upsert.isPending}
              className="flex-1 py-3 rounded-md bg-foreground text-background font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {upsert.isPending && <Loader2 className="size-4 animate-spin" />}
              {isEdit ? t("subs.saveChanges") : t("subs.addSubscription")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5 truncate">
        {label}
      </span>
      {children}
    </label>
  );
}

/* =================== Service combobox =================== */

function ServiceCombobox({
  services,
  loading,
  value,
  planCounts,
  onPick,
}: {
  services: ServiceWithCategory[];
  loading: boolean;
  value: ServiceWithCategory | null;
  planCounts: Map<string, number>;
  onPick: (svc: ServiceWithCategory) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const suppressToggleUntilRef = useRef(0);

  // Force-close whenever the selected value changes (defensive: ensures the
  // dropdown collapses after a pick even if a re-render races with setOpen).
  useEffect(() => {
    setOpen(false);
    setQ("");
  }, [value?.id]);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? services.filter(
          (s) =>
            s.name.toLowerCase().includes(needle) ||
            s.category_label.toLowerCase().includes(needle) ||
            s.category_key.toLowerCase().includes(needle),
        )
      : services;
    const map = new Map<string, { label: string; key: string; items: ServiceWithCategory[] }>();
    for (const s of filtered) {
      if (!map.has(s.category_key))
        map.set(s.category_key, { label: s.category_label, key: s.category_key, items: [] });
      map.get(s.category_key)!.items.push(s);
    }
    const orderIdx = new Map(CATEGORIES.map((c, i) => [c.value as string, i]));
    return Array.from(map.values()).sort(
      (a, b) => (orderIdx.get(a.key) ?? 999) - (orderIdx.get(b.key) ?? 999),
    );
  }, [services, q]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={(e) => {
          if (Date.now() < suppressToggleUntilRef.current) {
            e.preventDefault();
            e.stopPropagation();
            suppressToggleUntilRef.current = 0;
            return;
          }
          setOpen((o) => !o);
        }}
        className="relative w-full flex items-center input-base text-left h-10 pr-9"
      >
        <span className={value ? "" : "text-muted-foreground"}>
          {value ? value.name : t("subs.serviceSearchPlaceholder")}
        </span>
        <ChevronDown
          className={
            "absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground transition-transform " +
            (open ? "rotate-180" : "")
          }
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg overflow-hidden">
          <div className="relative border-b border-border">
            <Search
              className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("subs.serviceSearchPlaceholder")}
              className="w-full pl-9 pr-3 py-2.5 bg-transparent text-sm outline-none"
            />
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            {loading && <p className="text-sm text-muted-foreground p-3">{t("common.loading")}</p>}
            {!loading && grouped.length === 0 && (
              <p className="text-sm text-muted-foreground p-3">{t("subs.noResults")}</p>
            )}
            {grouped.map((g) => (
              <div key={g.key} className="py-1">
                <div className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground bg-card/40">
                  {t(`categories.${g.key}` as const, { defaultValue: g.label })}
                </div>
                <ul>
                  {g.items.map((s) => {
                    const n = planCounts.get(s.id) ?? 0;
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onPointerDown={() => {
                            suppressToggleUntilRef.current = Date.now() + 400;
                          }}
                          onClick={() => {
                            suppressToggleUntilRef.current = Date.now() + 400;
                            onPick(s);
                            setOpen(false);
                            setQ("");
                          }}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-foreground/5"
                        >
                          <span className="text-sm font-medium truncate">{s.name}</span>
                          <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                            {n === 1 ? t("subs.plansCountOne") : t("subs.plansCount", { n })}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== Plan pills =================== */

function PlanPills({
  plans,
  loading,
  selectedId,
  countryCurrency,
  onPick,
}: {
  plans: ServicePlan[];
  loading: boolean;
  selectedId: string | null;
  countryCurrency: string;
  onPick: (plan: ServicePlan) => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  }
  if (plans.length === 0) {
    return <p className="text-sm text-muted-foreground">—</p>;
  }
  return (
    <ul className="space-y-2">
      {plans.map((p) => {
        const active = selectedId === p.id;
        const currency = p.currency || countryCurrency || "NOK";
        return (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onPick(p)}
              className={
                "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-colors " +
                (active
                  ? "border-foreground bg-foreground/5"
                  : "border-border bg-background hover:border-foreground/30")
              }
            >
              <span className="text-sm font-semibold truncate">{p.name}</span>
              <span className="text-sm font-display font-bold tabular-nums shrink-0">
                {formatMoney(
                  monthlyEquivalent(Number(p.monthly_price_nok), p.billing_cycle),
                  currency,
                )}{" "}
                {t("common.perMonth")}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
